"use client";

import { useState } from "react";

import Link from "next/link";

import cn from "classnames";

import { BV_INLINE_RATING_PRODUCT_TYPES } from "@/components/bazaarvoice/bazaarvoice.constants";
import { BvWidgetContainer } from "@/components/bazaarvoice/bvWidget.container";
import { QueryLink } from "@/components/queryLink/queryLink.component";
import { SalesPrice } from "@/components/salesPrice/salesPrice.component";
import { Text } from "@/components/text/text.component";

import {
  buildEcommerceEvent,
  buildItemListId,
  buildItemListName,
  createEcommercePayload,
  mapProductToItem,
  mapProductTypeToAnalyticsCategory,
} from "@/lib/analytics/ecommerce-analytics";
import { setItemListOrigin } from "@/lib/analytics/item-list-context";

import { appendQueryString } from "@/utils/url.util";

import { brown } from "@/app/font";
import { EVENTS } from "@/constants/analytics.constants";
import { PRODUCT_DRAWER_QUERY_PARAM } from "@/constants/product.constants";
import { useSettings } from "@/contexts/settingsProvider.context";
import { useTagging } from "@/hooks/useTagging";
import type {
  Chip,
  ProductImage,
  RichText as SanityRichText,
} from "@/sanity.types";

import { ChevronRightIcon } from "../icons/chevronRight.icon";
import { PromoBadge } from "../promoBadge/promoBadge.component";
import { ProductTileColorVariant } from "./productTileColorVariant.component";
import { ProductTileImages } from "./productTileImages.component";
import { ProductTileOverlayChips } from "./productTileOverlayChips.component";
import { ProductTileTags } from "./productTileTags.component";

export const ProductTile = ({
  product,
  children,
  loading,
  viewDetails,
  hideDescriptionOnMobile,
  queryString = "",
  index,
  pageType = "",
  showRating,
}: ProductTileProps) => {
  const settings = useSettings();

  if (!product) return null;

  const [, setTag] = useTagging();

  const productHref = product.slug;

  const showBvRating = Boolean(
    showRating &&
    settings?.enableBazaarvoice &&
    !loading &&
    product.type &&
    BV_INLINE_RATING_PRODUCT_TYPES.includes(product.type),
  );

  const [selectedVariant, setSelectedVariant] = useState(
    () =>
      product?.options?.find((option) => option.variant === "color")
        ?.optionValues?.[0]?.product?.id ?? "",
  );

  const handleSelectItem = () => {
    const categoryParam = new URLSearchParams(queryString).get("category");
    const category = categoryParam || "all";
    const itemListId = buildItemListId(category);
    const itemListName = buildItemListName(category);

    setItemListOrigin({
      item_list_id: itemListId,
      item_list_name: itemListName,
      item_id: product.id,
      item_slug: product.slug,
    });

    const item = mapProductToItem(product, {
      index,
      category: mapProductTypeToAnalyticsCategory(product.type, product.id),
    });
    const payload = createEcommercePayload([item], {
      item_list_id: itemListId,
      item_list_name: itemListName,
    });

    setTag(buildEcommerceEvent(EVENTS.SELECT_ITEM, payload), { reset: true });
  };

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="product-tile"
      data-product-path={productHref}
      data-product-card-id={product.id}>
      <Link
        href={appendQueryString(productHref, queryString) ?? ""}
        data-testid="product-tile-link"
        aria-label={`View ${product?.name} product details`}
        onClick={handleSelectItem}>
        {loading ? (
          <div className="animate-pulse overflow-hidden rounded-2xl bg-gray-300">
            <svg xmlns="http://www.w3.org/2000/svg" width="fill" height="200">
              <rect width="100%" height="100%" fill="#d1d5dc" />
            </svg>
          </div>
        ) : (
          <div
            className="relative overflow-hidden rounded-2xl"
            style={{
              background:
                "radial-gradient(68.35% 68.36% at 50% 50%,rgba(146,230, 255, 0.30) 0%, rgba(184, 242, 255, 0.21) 50%, rgba(236, 249, 255, 0.24) 100%)",
              backdropFilter: "blur(4px)",
              boxShadow:
                "inset 1px 1px 0 rgba(255, 255, 255, 0.80),inset -1px -1px 0 rgba(180, 220, 235, 0.25)",
            }}>
            <ProductTileOverlayChips product={product} pageType={pageType} />
            <ProductTileImages
              product={product}
              selectedVariant={selectedVariant}
              pageType={pageType}
            />
          </div>
        )}
        <div className="mt-6 flex flex-col gap-2">
          {product.promotion?.tileCallout ? (
            <PromoBadge
              label={product.promotion?.tileCallout}
              testId="product-tile-promo-badge"
              iconSize={16}
            />
          ) : null}
          <Text
            variant="h6"
            component="h2"
            loading={loading}
            data-testid="product-tile-title">
            {product?.name}
          </Text>
          {showBvRating && product.id && (
            <BvWidgetContainer
              show="inline_rating"
              productId={product.id}
              className="empty:hidden"
            />
          )}
          <Text
            variant="body1"
            component="p"
            className={cn("line-clamp-3 md:line-clamp-none", {
              "hidden md:block": hideDescriptionOnMobile,
            })}
            loading={loading}
            loadingLines={2}
            data-testid="product-tile-description">
            {product?.shortDescription}
          </Text>
          <ProductTileTags product={product} loading={loading} />
        </div>
      </Link>
      {viewDetails && product?.id ? (
        <QueryLink
          data-testid="product-tile-view-details"
          set={{ [PRODUCT_DRAWER_QUERY_PARAM]: product.id }}
          className="inline-flex w-fit max-w-prose cursor-pointer items-center gap-1 underline-offset-2 hover:underline"
          aria-label={`View details for ${product.name ?? "product"}`}>
          <Text variant="body4" className="text-greyscale-body underline">
            View details
          </Text>
          <ChevronRightIcon
            width={16}
            height={16}
            className="mb-0.5 shrink-0"
          />
        </QueryLink>
      ) : null}
      <SalesPrice
        listPrice={product?.listPrice}
        salesPrice={product?.salesPrice}
        data-testid="product-price"
        salesPriceClassName={`${brown.className} headline-6`}
        className="mt-2"
        loading={loading}
        loadingClassName="w-1/4"
      />
      <ProductTileColorVariant
        options={product?.options}
        onSelectVariant={setSelectedVariant}
        selectedVariant={selectedVariant}
      />
      {children}
    </div>
  );
};

export type ProductTileProduct = {
  /** Commerce product id; pairs with `QuantityStepper` `id` for scoped `variantId`. */
  id?: string;
  /** App path from Sanity slug (e.g. `/shop/outdoor-camera`). */
  slug: string;
  name?: string;
  type?: string | null;
  shortDescription?: string | null;
  listPrice?: number | null;
  salesPrice?: number | null;
  options?: ProductTileOption[] | null;
  featuredImages?: ProductTileFeaturedImage[] | null;
  tags?: string[] | null;
  chips?: Chip[] | null;
  promotion?: {
    tileCallout?: SanityRichText | null;
    chips?: Chip[] | null;
  } | null;
};

type ProductTileProps = {
  children?: React.ReactNode;
  product?: ProductTileProduct;
  loading?: boolean;
  viewDetails?: boolean;
  hideDescriptionOnMobile?: boolean;
  queryString?: string;
  index?: number;
  pageType?: string;
  /** Opt in to the Bazaarvoice inline rating widget (PLP grid only). */
  showRating?: boolean;
};

export type ProductTileOption = {
  _key?: string;
  variant: "product" | "color";
  optionValues?: ProductTileOptionValue[] | null;
};

export type ProductTileOptionValue = {
  _key?: string;
  /** Stable option value identifier, normalised to a string by the query. */
  id?: string | null;
  title?: string | null;
  slug?: string | null;
  image?: Array<{
    asset?: unknown;
    media?: unknown;
    hotspot?: unknown;
    crop?: unknown;
    _type: "image";
    _key?: string;
  }> | null;
  product?: {
    id?: string;
    slug?: string;
    url?: string;
    featuredImages?: ProductTileFeaturedImage[] | null;
  } | null;
};
export type ProductTileFeaturedImage = Omit<
  ProductImage,
  "alt" | "image" | "pageType"
> & {
  _key?: string;
  alt?: string | null;
  image: {
    _type: "image";
    asset?: ProductImage["image"]["asset"] | null;
    media?: unknown;
    hotspot?: ProductImage["image"]["hotspot"] | null;
    crop?: ProductImage["image"]["crop"] | null;
  };
  pageType?: string | null;
};
